import { useLazyQuery, useMutation } from "@apollo/client/react";
import { FC, ReactElement, cloneElement, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Stack } from "@mui/material";
import { toast } from "react-toastify";
import { CREDENTIAL_TYPES_BY_NAME } from "../../../components";
import { useAccountContext } from "../../../strict/app/AccountResolver";
import { KeyEditorActionModal } from "../../../features/keys/components/KeyEditorActionModal";
import type {
  SaveCredentialMutation,
  SaveCredentialMutationVariables,
  StartKeyOauthQuery,
  StartKeyOauthQueryVariables,
} from "../../../graphql/types";
import { Credential, SaveCredentialDocument, StartKeyOauthDocument, ValidationErrors } from "../../../graphql/types";
import { EditorFormProps, EditorFormWithMutationProps } from "./indexTypes";

interface IPreparePayload {
  credential_type?: string;
  original_name?: string;
  mode?: string;
  credential: Credential;
}

const preparePayload = ({ credential_type, original_name, mode, credential }: IPreparePayload) => {
  const properties = { ...credential.properties }; //copy
  const variables: SaveCredentialMutationVariables = {
    properties: properties,
    credential_type: credential_type || credential.credential_type,
    new_name: credential["name"],
    replace_properties: mode === "add",
  };
  if (mode === "edit") {
    variables["name"] = original_name;
    variables["status"] = "active";
  } else {
    // let server determine initial status by credential type
  }
  return variables;
};

const OauthEditorForm: FC<EditorFormProps> = ({
  loading,
  credential,
  credential_type,
  mode,
  onError,
  onCompleted,
  onLeave,
}) => {
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const isNewCredential = !credential?.id;
  const [getOauthURL, { loading: loadingQuery }] = useLazyQuery<StartKeyOauthQuery, StartKeyOauthQueryVariables>(
    StartKeyOauthDocument,
    {
      errorPolicy: "all",
      fetchPolicy: "no-cache",
    }
  );
  const editor = credential_type ? (CREDENTIAL_TYPES_BY_NAME[credential_type]?.editor ?? null) : null;
  let editorWithProps: ReactElement | null = null;

  if (editor) {
    editorWithProps = cloneElement(editor, {
      onLeave: onLeave,
      loading: loading || loadingQuery,
      mode: mode,
      validationErrors: validationErrors,
      credential_type: credential_type,
      credential: credential,
      onFormSubmit: async (values: Credential) => {
        const { data, error } = await getOauthURL({
          variables: {
            name: values.name,
            oauthType: credential_type || "",
            entity: "credential",
            ...(credential?.id ? { id: credential.id.toString() } : {}),
          },
        });
        if (error) {
          onError(error);
        } else if (data) {
          if (isNewCredential && data.validateCredentialUniqueName?.__typename === "ValidationErrors") {
            setValidationErrors(data.validateCredentialUniqueName);
          } else if (onCompleted) {
            onCompleted({
              name: credential?.name || undefined,
              oauth_url: data.start_oauth || undefined,
              exist: false,
            });
          }
        }
      },
    });
  }

  return editorWithProps;
};

const EditorForm: FC<EditorFormProps> = ({
  loading,
  credential,
  credential_type,
  original_name,
  mode,
  submitMode,
  onError,
  onCompleted,
  onLeave,
}) => {
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [saveCredential, { loading: loadingMutation }] = useMutation<
    SaveCredentialMutation,
    SaveCredentialMutationVariables
  >(SaveCredentialDocument, {
    refetchQueries: ["credentials"],
    awaitRefetchQueries: false,
    errorPolicy: "all",
    onError: (error) => onError(error),
    onCompleted: (data) => {
      if (onCompleted && data.save_credential?.__typename === "SaveCredentialOk") {
        onCompleted({
          name: data.save_credential.credential?.name,
        });
      } else if (data.save_credential?.__typename === "ValidationErrors") {
        setValidationErrors(data.save_credential);
      }
    },
  });

  const editor = credential_type ? (CREDENTIAL_TYPES_BY_NAME[credential_type]?.editor ?? null) : null;
  let editorWithProps: ReactElement | null = null;

  if (editor) {
    editorWithProps = cloneElement(editor, {
      loading: loading || loadingMutation,
      onLeave: onLeave,
      mode: mode,
      validationErrors: validationErrors,
      credential_type: credential_type,
      credential: credential,
      onFormSubmit: (values: Credential) => {
        Object.keys(values?.properties || {}).forEach((key) => {
          if (values.properties[key] === "") {
            values.properties[key] = null;
          }
        });

        const payload = preparePayload({
          credential_type: credential_type,
          original_name: original_name || credential?.name,
          mode: submitMode,
          credential: values,
        });
        saveCredential({ variables: payload });
      },
    });
  }

  return editorWithProps;
};

export const EditorFormWithMutation: FC<EditorFormWithMutationProps> = ({
  credential,
  mode,
  onCompleted,
  original_name,
  keyType,
}) => {
  const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false);
  const { urlName } = useAccountContext();

  const credentialType = keyType || credential?.credential_type || "";

  const navigate = useNavigate();

  const keyDetailsRedirect = `/app/${urlName}/keys`;

  const onError = (error: Error) => {
    console.error("There was an unexpected error saving the credential:", error);
    toast.error("There was an unexpected error saving the credential.");
  };

  const isOauth = CREDENTIAL_TYPES_BY_NAME[credentialType]?.isOauth;
  return (
    <Stack spacing={2}>
      <KeyEditorActionModal
        open={showConfirmCancelModal}
        onCancel={() => setShowConfirmCancelModal(false)}
        buttonAction={() => navigate(keyDetailsRedirect)}
        body="If you exit now, the changes won’t be saved and you’ll lose the data."
        title="Are you sure?"
        buttonText="Leave"
        onClose={() => setShowConfirmCancelModal(false)}
      />

      {isOauth && mode !== "edit" ? (
        <OauthEditorForm
          mode={mode}
          credential={credential}
          credential_type={credentialType}
          onError={onError}
          onCompleted={onCompleted}
          onLeave={() => setShowConfirmCancelModal(true)}
        />
      ) : (
        <EditorForm
          mode={mode}
          credential={credential}
          credential_type={credentialType}
          original_name={original_name}
          onError={onError}
          onCompleted={onCompleted}
          submitMode={mode === "edit" ? "edit" : undefined}
          onLeave={() => setShowConfirmCancelModal(true)}
        />
      )}
    </Stack>
  );
};
